/**
 * composeRegistry — singleton bridge between ComposeRibbon and the
 * contentEditable editor.
 *
 * Unified formatting model:
 *  - ALL style changes go through applyStyle(styleObject).
 *  - Bold, italic, underline, strikethrough are CSS properties, not HTML tags.
 *  - execCommand is only used for block-level commands (align, lists).
 *  - Style merging: Object.assign on spans — never replaces existing properties.
 *  - text-decoration is treated as an additive set (underline + line-through coexist).
 *
 * Undo / redo:
 *  - All formatting ops push a snapshot before mutating the DOM.
 *  - Text typing pushes a debounced snapshot (via pushUndoSnapshot from ComposeMail).
 *  - Ctrl+Z / Ctrl+Y in the editor, and the ribbon buttons, call undoHistory /
 *    redoHistory which restore innerHTML + cursor using node-path encoding.
 */

// ─── Editor ref ───────────────────────────────────────────────────────────────

/** @type {(() => HTMLElement | null) | null} */
let getEditorEl = null;

// ─── Selection save / restore ─────────────────────────────────────────────────

/** Range saved on mousedown of dropdown triggers (before editor loses focus). */
let savedRange = null;

export function saveSelection() {
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0) {
    savedRange = sel.getRangeAt(0).cloneRange();
  }
}

// Auto-update savedRange on every selectionchange while the editor has a live
// non-collapsed selection. This ensures button clicks (bold, italic, underline)
// that steal focus from the editor still operate on the correct selection range,
// without requiring manual saveSelection() calls on every control's mousedown.
if (typeof document !== 'undefined') {
  document.addEventListener('selectionchange', () => {
    const el = getEditorEl?.();
    if (!el) return;
    const sel = window.getSelection();
    if (
      sel &&
      sel.rangeCount > 0 &&
      !sel.isCollapsed &&
      el.contains(sel.anchorNode)
    ) {
      savedRange = sel.getRangeAt(0).cloneRange();
    }
  });
}

export function restoreSelection() {
  if (!savedRange) return;
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(savedRange);
}

// ─── Registry lifecycle ───────────────────────────────────────────────────────

export const registry = {
  submitForm:     null,
  handleClose:    null,
  handleDiscard:  null,
  saveDraft:      null,
  triggerAttach:  null,
};

export function registerCompose({ getEditor, submitForm, handleClose, handleDiscard, saveDraft, triggerAttach }) {
  getEditorEl             = getEditor;
  registry.submitForm     = submitForm;
  registry.handleClose    = handleClose;
  registry.handleDiscard  = handleDiscard;
  registry.saveDraft      = saveDraft;
  registry.triggerAttach  = triggerAttach;
}

export function unregisterCompose() {
  getEditorEl             = null;
  registry.submitForm     = null;
  registry.handleClose    = null;
  registry.handleDiscard  = null;
  registry.saveDraft      = null;
  registry.triggerAttach  = null;
}

/** Call from ribbon / EmailLayout to trigger a manual draft save. */
export function saveDraft() {
  registry.saveDraft?.();
}

/** Call from ribbon / EmailLayout to discard compose (deletes draft if one exists). */
export function discardCompose() {
  registry.handleDiscard?.();
}

// ─── Zone-aware HTML extractors ───────────────────────────────────────────────

/**
 * Returns ONLY the user-authored HTML from the editor — safe to send to Graph API.
 *
 * For reply/replyAll/forward editors:
 *   Reads [data-compose-zone="user"] innerHTML only.
 *   The history zone (quoted reply / forwarded body) is excluded.
 *   Graph /reply, /replyAll, /forward all append the original thread automatically.
 *
 * For new-email editors:
 *   No history zone exists → returns the full innerHTML (correct: no duplication risk).
 */
export function getPayloadHtml() {
  const el = getEditorEl?.();
  if (!el) return '';
  const userZone = el.querySelector('[data-compose-zone="user"]');
  return userZone ? userZone.innerHTML : el.innerHTML;
}

/**
 * Returns the full display HTML (user zone + history zone).
 * Used only for popout transfer — preserves the complete visual state.
 * Never send this to the Graph API.
 */
export function getDisplayHtml() {
  const el = getEditorEl?.();
  return el ? el.innerHTML : '';
}

// ─── execOnCompose — block-level commands (align, lists, formatBlock, links) ──
// These go through document.execCommand which is undo-tracked by the browser,
// but since we intercept Ctrl+Z ourselves we also push to our own stack first.

const EXEC_NO_SNAPSHOT = new Set(['undo', 'redo']); // internal; not user formatting

export function execOnCompose(cmd, val = null) {
  const el = getEditorEl?.();
  if (!el) return;
  if (!EXEC_NO_SNAPSHOT.has(cmd)) pushUndoSnapshot();
  el.focus();
  document.execCommand(cmd, false, val ?? undefined);
}

// ─── Focus + selection restore helper ────────────────────────────────────────

/**
 * Focus the editor and, if it didn't already have a live non-collapsed
 * selection, restore the saved range.
 *
 * IMPORTANT: The "editorWasActive" check MUST happen BEFORE el.focus().
 * Calling el.focus() collapses the browser selection, making a post-focus
 * check always return "active=true, collapsed=true", which skips restore.
 *
 * Returns { el, range } or { el: null, range: null } if nothing to work on.
 */
function focusAndRestoreSelection() {
  const el = getEditorEl?.();
  if (!el) return { el: null, range: null };

  const preFocusSel = window.getSelection();
  const editorWasActive =
    document.activeElement === el &&
    preFocusSel && preFocusSel.rangeCount > 0 &&
    el.contains(preFocusSel.anchorNode) &&
    !preFocusSel.isCollapsed;

  el.focus();

  if (!editorWasActive) restoreSelection();

  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return { el, range: null };
  return { el, range: sel.getRangeAt(0) };
}

// ─── DOM traversal helpers ────────────────────────────────────────────────────

/**
 * All text nodes that intersect `range`, handling the single-text-node case.
 */
function getTextNodesInRange(range) {
  const root = range.commonAncestorContainer;
  if (root.nodeType === Node.TEXT_NODE) return [root];

  const nodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  while (walker.nextNode()) {
    if (range.intersectsNode(walker.currentNode)) {
      nodes.push(walker.currentNode);
    }
  }
  return nodes;
}

/**
 * Walk up from `node` to find the nearest ancestor <span> with inline style,
 * stopping at the editor root.
 */
function getContainingStyledSpan(node, editorEl) {
  let el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  while (el && el !== editorEl) {
    if (el.tagName === 'SPAN' && el.style && el.style.cssText) return el;
    el = el.parentElement;
  }
  return null;
}

function isRangeFullyInside(range, element) {
  return element.contains(range.startContainer) &&
         element.contains(range.endContainer);
}

// ─── Style merging ────────────────────────────────────────────────────────────

/**
 * Walk up from `node` collecting every inline CSS property set on ancestor
 * <span> elements, stopping at the editor root.  Deeper spans (closer to the
 * text node) take precedence — matching what the browser would compute.
 *
 * Called BEFORE range.extractContents() so the text node is still attached to
 * its styled parent.  After extraction the parent link is severed and this
 * information would be lost.
 *
 * @param {Node}        node      The text node (or element) whose ancestors to walk
 * @param {HTMLElement} editorEl  Editor root — stop here
 * @returns {Object}   Plain object of camelCase CSS properties → string values
 */
function collectInlineStylesFromAncestors(node, editorEl) {
  const styles = {};
  const PROPS = [
    'fontFamily', 'fontSize', 'color', 'backgroundColor',
    'fontWeight', 'fontStyle', 'textDecoration',
  ];
  let el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  while (el && el !== editorEl) {
    if (el.tagName === 'SPAN' && el.style && el.style.cssText) {
      for (const prop of PROPS) {
        // Deeper spans win — only record if not already captured
        if (el.style[prop] && !(prop in styles)) {
          styles[prop] = el.style[prop];
        }
      }
    }
    el = el.parentElement;
  }
  return styles;
}

/**
 * Merge `newStyle` on top of `baseStyle`, producing a single resolved style
 * object safe to hand straight to `buildSpanStyle`.
 *
 * textDecoration is handled additively:
 *   base  = 'underline line-through'
 *   new   = '-underline'   → result = 'line-through'   (removal respected)
 *   new   = 'underline'    → result = 'underline line-through'  (additive)
 *
 * All other properties: newStyle wins outright.
 *
 * @param {Object} baseStyle  Inherited styles from ancestor spans
 * @param {Object} newStyle   The user's formatting action
 * @returns {Object}
 */
function buildEffectiveStyle(baseStyle, newStyle) {
  const result = { ...baseStyle };
  for (const [key, val] of Object.entries(newStyle)) {
    if (key === 'textDecoration') {
      const existing = (result.textDecoration || '')
        .split(/\s+/).filter(p => p && p !== 'none');
      if (!val || val === 'none') {
        delete result.textDecoration;
      } else if (val.startsWith('-')) {
        const toRemove = val.slice(1);
        const filtered = existing.filter(d => d !== toRemove);
        if (filtered.length) result.textDecoration = filtered.join(' ');
        else delete result.textDecoration;
      } else {
        if (!existing.includes(val)) existing.push(val);
        result.textDecoration = existing.join(' ');
      }
    } else {
      result[key] = val;
    }
  }
  return result;
}

/**
 * Merge `styleObject` into a span's inline style with special handling for
 * text-decoration (additive set: underline + line-through can coexist).
 *
 * Convention for textDecoration value:
 *   'underline'      → add underline
 *   'line-through'   → add line-through
 *   '-underline'     → remove underline  (leave line-through intact)
 *   '-line-through'  → remove line-through (leave underline intact)
 */
function mergeIntoSpanStyle(span, styleObject) {
  for (const [key, val] of Object.entries(styleObject)) {
    if (key === 'textDecoration') {
      const existing = (span.style.textDecoration || '')
        .split(/\s+/).filter(p => p && p !== 'none');

      if (!val || val === 'none') {
        span.style.textDecoration = '';
      } else if (val.startsWith('-')) {
        const toRemove = val.slice(1);
        const filtered = existing.filter(d => d !== toRemove);
        span.style.textDecoration = filtered.length ? filtered.join(' ') : '';
      } else {
        if (!existing.includes(val)) existing.push(val);
        span.style.textDecoration = existing.join(' ');
      }
    } else {
      span.style[key] = val;
    }
  }
}

/**
 * Apply `styleObject` to a fresh span wrapping a text node.
 * Skips removal-prefixed text-decoration values (nothing to remove on a new span).
 */
function buildSpanStyle(span, styleObject) {
  for (const [key, val] of Object.entries(styleObject)) {
    if (key === 'textDecoration') {
      if (val && !val.startsWith('-') && val !== 'none') {
        span.style.textDecoration = val;
      }
    } else {
      span.style[key] = val;
    }
  }
}

/**
 * Recursively walk a DOM subtree and apply `styleObject` to every node:
 *  - Text node  → wrap in <span> whose style = baseStyles merged with styleObject
 *                 (baseStyles carries the inline styles that were on the ancestor
 *                 span BEFORE extractContents severed the parent link — this is
 *                 what prevents font-family / color / size from being lost)
 *  - <span>     → merge styleObject into existing inline style only (the span
 *                 already has its own styles; baseStyles must NOT overwrite them)
 *  - Other tags → recurse into children, forwarding both style args
 *
 * @param {Node}   node        DOM node from the extracted fragment
 * @param {Object} styleObject The user's new formatting (e.g. {fontWeight:'bold'})
 * @param {Object} baseStyles  Inherited styles from the pre-extraction ancestor chain
 */
function mergeStyleIntoNode(node, styleObject, baseStyles = {}) {
  if (node.nodeType === Node.TEXT_NODE) {
    if (!node.textContent) return;
    const span = document.createElement('span');
    // Resolve the complete style: inherited base first, new style on top.
    // buildEffectiveStyle handles additive textDecoration merge / removal.
    buildSpanStyle(span, buildEffectiveStyle(baseStyles, styleObject));
    node.parentNode.insertBefore(span, node);
    span.appendChild(node);
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;

  if (node.tagName === 'SPAN') {
    // Existing span already carries its own styles — apply only the new
    // style delta, never the inherited base (that would overwrite e.g. a
    // different font family on a nested span).
    mergeIntoSpanStyle(node, styleObject);
    return;
  }

  // <b>, <i>, <u>, <s>, <div>, <p>, etc. — recurse, forwarding baseStyles
  Array.from(node.childNodes).forEach(child =>
    mergeStyleIntoNode(child, styleObject, baseStyles));
}

// ─── Core applyStyle ──────────────────────────────────────────────────────────

/**
 * Apply `styleObject` to the editor selection, merging with ALL existing inline
 * styles. This is the single entry point for every style change.
 *
 * @param {Partial<CSSStyleDeclaration>} styleObject
 */
export function applyStyle(styleObject) {
  // Snapshot BEFORE mutation so Ctrl+Z can restore the pre-formatting state
  pushUndoSnapshot();

  const { el, range } = focusAndRestoreSelection();
  if (!el || !range) return;

  const sel = window.getSelection();

  // ── Case 1: collapsed cursor ─────────────────────────────────────────────
  if (range.collapsed) {
    const containingSpan = getContainingStyledSpan(range.startContainer, el);
    if (containingSpan) {
      mergeIntoSpanStyle(containingSpan, styleObject);
    } else {
      // Insert a zero-width span so the next typed character inherits the style
      const zws = document.createElement('span');
      buildSpanStyle(zws, styleObject);
      zws.textContent = '\u200B';
      range.insertNode(zws);
      const nr = document.createRange();
      nr.setStartAfter(zws);
      nr.collapse(true);
      sel.removeAllRanges();
      sel.addRange(nr);
    }
    return;
  }

  // ── Case 2: entire selection inside one styled span ──────────────────────
  // Only use the whole-span fast path when the selection covers ALL content
  // of the span. Partial selections fall through to Case 3 so only the
  // selected portion gets the new style, not the surrounding text.
  const ancestor = range.commonAncestorContainer;
  const containingSpan = getContainingStyledSpan(ancestor, el);
  if (containingSpan && isRangeFullyInside(range, containingSpan)) {
    const spanRange = document.createRange();
    spanRange.selectNodeContents(containingSpan);
    const coversAll =
      range.compareBoundaryPoints(Range.START_TO_START, spanRange) <= 0 &&
      range.compareBoundaryPoints(Range.END_TO_END, spanRange) >= 0;
    if (coversAll) {
      mergeIntoSpanStyle(containingSpan, styleObject);
      const preserved = range.cloneRange();
      sel.removeAllRanges();
      sel.addRange(preserved);
      return;
    }
    // Partial selection inside a span — fall through to Case 3 to wrap only
    // the selected portion.
  }

  // ── Case 3: multi-node / cross-span selection ────────────────────────────
  // Capture inherited inline styles from the selection start BEFORE extraction.
  // extractContents() severs text nodes from their parent spans — after that
  // the ancestor chain is gone and font-family / color / size can't be recovered.
  // We snapshot it now so mergeStyleIntoNode can re-apply it as a base when
  // wrapping bare text nodes in new spans.
  const inheritedStyles = collectInlineStylesFromAncestors(range.startContainer, el);

  const fragment = range.extractContents();
  Array.from(fragment.childNodes).forEach(child =>
    mergeStyleIntoNode(child, styleObject, inheritedStyles));

  // Save before insertNode empties the fragment
  const insertedNodes = Array.from(fragment.childNodes);
  range.insertNode(fragment);

  if (insertedNodes.length > 0) {
    try {
      const nr = document.createRange();
      nr.setStartBefore(insertedNodes[0]);
      nr.setEndAfter(insertedNodes[insertedNodes.length - 1]);
      sel.removeAllRanges();
      sel.addRange(nr);
      savedRange = nr.cloneRange();
    } catch (_) { /* ignore invalid range after complex DOM ops */ }
  }
}

// ─── Toggle helpers ───────────────────────────────────────────────────────────

/**
 * Check if a CSS property has a uniform value across all selected text nodes.
 * Returns the common value string, or null if mixed or no selection.
 */
function getUniformComputedValue(range, cssProperty) {
  if (range.collapsed) return null;
  const textNodes = getTextNodesInRange(range).filter(n => n.textContent.trim());
  if (!textNodes.length) return null;

  let first = null;
  for (const node of textNodes) {
    const parent = node.nodeType === Node.TEXT_NODE
      ? (node.parentElement ?? node)
      : node;
    const val = window.getComputedStyle(parent)[cssProperty] ?? '';
    if (first === null) first = val;
    else if (val !== first) return null; // mixed — treat as off
  }
  return first;
}

/**
 * Toggle a simple CSS property. If the entire selection already has `onValue`,
 * applies `offValue`; otherwise applies `onValue`.
 *
 * @param {string} cssProperty  camelCase CSS property
 * @param {string} onValue      value to apply when toggling on
 * @param {string} offValue     value to apply when toggling off
 * @param {(val: string) => boolean} isOn  predicate: is the current value "on"?
 */
function toggleStyle(cssProperty, onValue, offValue, isOn) {
  const { el, range } = focusAndRestoreSelection();
  if (!el || !range) return;

  let active;
  if (range.collapsed) {
    // Cursor: inspect containing span or fall back to queryCommandState
    const span = getContainingStyledSpan(range.startContainer, el);
    if (span) {
      active = isOn(window.getComputedStyle(span)[cssProperty] ?? '');
    } else {
      // queryCommandState still works for cursor typing state
      const cmdMap = { fontWeight: 'bold', fontStyle: 'italic' };
      const cmd = cmdMap[cssProperty];
      active = cmd ? document.queryCommandState(cmd) : false;
    }
  } else {
    const val = getUniformComputedValue(range, cssProperty);
    active = val !== null && isOn(val);
  }

  applyStyle({ [cssProperty]: active ? offValue : onValue });
}

/**
 * Toggle a text-decoration keyword (additive).
 * If ALL selected text already has `decoration`, removes just that decoration
 * (leaving others like line-through intact). Otherwise adds it.
 */
function toggleTextDecoration(decoration) {
  const { el, range } = focusAndRestoreSelection();
  if (!el || !range) return;

  let active;
  if (range.collapsed) {
    const span = getContainingStyledSpan(range.startContainer, el);
    if (span) {
      active = (window.getComputedStyle(span).textDecoration || '').includes(decoration);
    } else {
      const cmdMap = { 'underline': 'underline', 'line-through': 'strikeThrough' };
      const cmd = cmdMap[decoration];
      active = cmd ? document.queryCommandState(cmd) : false;
    }
  } else {
    const textNodes = getTextNodesInRange(range).filter(n => n.textContent.trim());
    active = textNodes.length > 0 && textNodes.every(node => {
      const parent = node.parentElement ?? node;
      return (window.getComputedStyle(parent).textDecoration || '').includes(decoration);
    });
  }

  // '-' prefix = remove; no prefix = add (see mergeIntoSpanStyle)
  applyStyle({ textDecoration: active ? `-${decoration}` : decoration });
}

// ─── Public formatting API ────────────────────────────────────────────────────

/** Toggle bold — uses font-weight, never <b> tags. */
export function applyBold() {
  toggleStyle(
    'fontWeight', 'bold', 'normal',
    v => v === 'bold' || parseInt(v, 10) >= 700,
  );
}

/** Toggle italic — uses font-style, never <i> tags. */
export function applyItalic() {
  toggleStyle(
    'fontStyle', 'italic', 'normal',
    v => v === 'italic',
  );
}

/** Toggle underline — additive with line-through. */
export function applyUnderline() {
  toggleTextDecoration('underline');
}

/** Toggle strikethrough — additive with underline. */
export function applyStrikethrough() {
  toggleTextDecoration('line-through');
}

/** Apply font family — merges with all existing styles. */
export function applyFontFamily(name) {
  applyStyle({ fontFamily: name });
}

/** Apply font size in pt — merges with all existing styles. */
export function applyFontSize(pt) {
  applyStyle({ fontSize: `${pt}pt` });
}

// ─── Undo / Redo History ──────────────────────────────────────────────────────
//
// Every formatting operation calls pushUndoSnapshot() BEFORE mutating the DOM.
// ComposeMail also calls it from a debounced onInput handler so text typing is
// undoable in coarse chunks (not per-keystroke).
//
// Cursor position is encoded as a "node path" — an array of child-node indices
// from the editor root to the selection container.  After innerHTML is restored
// the DOM structure is identical to when the snapshot was taken, so the same
// path resolves to the same structural node.
// ─────────────────────────────────────────────────────────────────────────────

const HISTORY_LIMIT = 100;
const _undoStack    = [];
const _redoStack    = [];
let   _skipSnapshot = false; // prevents recursive snapshotting during undo/redo

/** Walk upward from `node` to `root`, recording each child index. */
function _nodePath(root, node) {
  const path = [];
  let cur = node;
  while (cur && cur !== root) {
    const parent = cur.parentNode;
    if (!parent) return null;
    path.unshift(Array.prototype.indexOf.call(parent.childNodes, cur));
    cur = parent;
  }
  return cur === root ? path : null; // null if node wasn't inside root
}

/** Resolve a stored path back to a DOM node after innerHTML restore. */
function _nodeAt(root, path) {
  if (!path) return null;
  let node = root;
  for (const i of path) {
    if (!node.childNodes || i < 0 || i >= node.childNodes.length) return null;
    node = node.childNodes[i];
  }
  return node;
}

/** Snapshot the current Selection inside `el`. Returns null if none. */
function _captureSel(el) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const r = sel.getRangeAt(0);
  if (!el.contains(r.startContainer)) return null;
  return {
    sp: _nodePath(el, r.startContainer), so: r.startOffset,
    ep: _nodePath(el, r.endContainer),   eo: r.endOffset,
  };
}

/** Re-apply a stored selection snapshot after innerHTML has been restored. */
function _applySel(el, s) {
  if (!s) return;
  try {
    const sc = _nodeAt(el, s.sp);
    const ec = _nodeAt(el, s.ep);
    if (!sc || !ec) return;
    const maxSo = sc.nodeType === Node.TEXT_NODE ? sc.length : sc.childNodes.length;
    const maxEo = ec.nodeType === Node.TEXT_NODE ? ec.length : ec.childNodes.length;
    const r = document.createRange();
    r.setStart(sc, Math.min(s.so, maxSo));
    r.setEnd(ec,   Math.min(s.eo, maxEo));
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(r);
    savedRange = r.cloneRange();
  } catch (_) { /* ignore — cursor at end is better than a throw */ }
}

/**
 * Push a snapshot of the editor's current innerHTML + cursor onto the undo
 * stack.  Call this BEFORE any DOM mutation you want to be undoable.
 *
 * Safe to call speculatively — deduplicates consecutive identical states so
 * rapid calls (e.g., from a debounced input handler) don't bloat the stack.
 *
 * Exported so ComposeMail can call it from its debounced onInput handler.
 */
export function pushUndoSnapshot() {
  if (_skipSnapshot) return;
  const el = getEditorEl?.();
  if (!el) return;
  const html = el.innerHTML;
  if (_undoStack.length > 0 && _undoStack[_undoStack.length - 1].html === html) return;
  _undoStack.push({ html, sel: _captureSel(el) });
  if (_undoStack.length > HISTORY_LIMIT) _undoStack.shift();
  _redoStack.length = 0; // any new action clears the redo branch
}

/** Undo the last recorded change. */
export function undoHistory() {
  const el = getEditorEl?.();
  if (!el || _undoStack.length === 0) return;
  _redoStack.push({ html: el.innerHTML, sel: _captureSel(el) });
  const snap = _undoStack.pop();
  _skipSnapshot = true;
  el.innerHTML = snap.html;
  _skipSnapshot = false;
  _applySel(el, snap.sel);
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

/** Redo the last undone change. */
export function redoHistory() {
  const el = getEditorEl?.();
  if (!el || _redoStack.length === 0) return;
  _undoStack.push({ html: el.innerHTML, sel: _captureSel(el) });
  const snap = _redoStack.pop();
  _skipSnapshot = true;
  el.innerHTML = snap.html;
  _skipSnapshot = false;
  _applySel(el, snap.sel);
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

// ─── HTML insertion ───────────────────────────────────────────────────────────

/**
 * Parse an HTML string into a DocumentFragment without touching the DOM.
 * Uses a detached <template> element so no scripts execute and no images load.
 */
function parseHtml(html) {
  const t = document.createElement('template');
  t.innerHTML = html;
  return t.content; // DocumentFragment, safe and inert
}

/**
 * Insert arbitrary HTML at the current cursor position inside the user zone.
 *
 * Algorithm (no execCommand):
 *  1. Restore saved selection if editor lost focus (e.g. after ribbon click).
 *  2. Clamp the range to the user zone — if the cursor is outside it (e.g. in
 *     the history/quoted zone) we move it to the end of the user zone instead.
 *  3. Delete any selected text (replace-on-type behaviour).
 *  4. Build a DocumentFragment from the HTML string.
 *  5. Insert via range.insertNode() — no deprecated APIs.
 *  6. Advance the cursor to just after the last inserted node.
 *  7. Save the resulting range so subsequent ribbon actions work.
 *
 * @param {string} html  Valid HTML string to insert.
 */
export function insertHtmlAtCursor(html) {
  const el = getEditorEl?.();
  if (!el || !html) return;

  // Snapshot before insertion so it can be undone
  pushUndoSnapshot();

  // ── 1. Focus + restore selection ────────────────────────────────────────────
  const preFocusSel = window.getSelection();
  const editorWasActive =
    document.activeElement === el &&
    preFocusSel && preFocusSel.rangeCount > 0 &&
    el.contains(preFocusSel.anchorNode);

  el.focus();
  if (!editorWasActive) restoreSelection();

  const sel = window.getSelection();

  // ── 2. Resolve a range clamped to the user zone ─────────────────────────────
  const userZone = el.querySelector('[data-compose-zone="user"]') || el;
  let range;

  if (sel && sel.rangeCount > 0) {
    const r = sel.getRangeAt(0);
    // Use the range only when it's inside the user zone
    if (userZone.contains(r.startContainer)) {
      range = r;
    }
  }

  if (!range) {
    // No usable cursor — fall back to end of user zone
    range = document.createRange();
    range.selectNodeContents(userZone);
    range.collapse(false);
  }

  // ── 3. Delete selected text (makes insertion feel like a replace) ────────────
  if (!range.collapsed) range.deleteContents();

  // ── 4. Build fragment ────────────────────────────────────────────────────────
  const frag = parseHtml(html);
  // Collect inserted nodes before the fragment empties on insertNode
  const insertedNodes = Array.from(frag.childNodes);

  // ── 5. Insert ────────────────────────────────────────────────────────────────
  range.insertNode(frag);

  // ── 6. Advance cursor ───────────────────────────────────────────────────────
  if (insertedNodes.length > 0) {
    try {
      const newRange = document.createRange();
      newRange.setStartAfter(insertedNodes[insertedNodes.length - 1]);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
      savedRange = newRange.cloneRange(); // ── 7. persist
    } catch (_) { /* complex DOM — leave cursor wherever browser put it */ }
  }
}

/**
 * Insert (or replace) the email signature in the user zone.
 *
 * Outlook signature rules:
 *  - Signature always lives at the END of the compose area — never mid-text.
 *  - If a .bpa-signature element already exists, it is replaced in-place.
 *  - After insertion, cursor is parked in the body ABOVE the signature so the
 *    user can keep typing without having to click.
 *
 * @param {string} sigHtml  Full HTML of the signature (no wrapper needed).
 */
export function insertSignatureHtml(sigHtml) {
  const el = getEditorEl?.();
  if (!el) return;

  el.focus();

  const userZone = el.querySelector('[data-compose-zone="user"]') || el;

  // ── Remove existing signature ────────────────────────────────────────────────
  const existing = userZone.querySelector('.bpa-signature');
  if (existing) existing.remove();

  if (!sigHtml) return; // caller passed empty string = "remove signature"

  // ── Build and append new signature node ─────────────────────────────────────
  const frag = parseHtml(sigHtml);
  userZone.appendChild(frag); // frag empties into userZone

  // ── Ensure there is a paragraph BEFORE the signature to type in ─────────────
  // If the node immediately before the signature is not a <p> or <div>, insert one.
  const sigEl = userZone.querySelector('.bpa-signature');
  if (sigEl) {
    const prev = sigEl.previousSibling;
    const isTypeable =
      prev &&
      prev.nodeType === Node.ELEMENT_NODE &&
      /^(P|DIV|BR)$/i.test(prev.nodeName);

    if (!isTypeable) {
      const p = document.createElement('p');
      p.innerHTML = '<br>';
      userZone.insertBefore(p, sigEl);
    }
  }

  // ── Park cursor at end of the typing area (before signature) ────────────────
  try {
    const anchor = sigEl ? sigEl.previousSibling : null;
    const range  = document.createRange();
    if (anchor) {
      range.setStartAfter(anchor);
    } else {
      range.setStart(userZone, 0);
    }
    range.collapse(true);

    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    savedRange = range.cloneRange();
  } catch (_) { /* ignore */ }
}
